import { useEffect, useState } from 'react';
import { Landmark, Loader2, Download, AlertTriangle, Check, SendHorizontal } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import Dropdown from '../receptura/Dropdown';
import { ACCENT as SECTION_COLOR, OK, WARN, BAD } from '../../lib/uiTokens';

/**
 * Export vybraných dokladů jako hromadný příkaz pro banku (ABO / .kpc).
 *
 * Soubor se nahraje v George (Platby → nahrát soubor) a tam se dávka jen
 * autorizuje. Po stažení nabídne razítko „zadáno do banky" pro doklady, které
 * v souboru jsou — razítko se nedává samo, soubor se ještě nemusí nahrát.
 * Zaplaceno potom potvrdí bankovní výpis (kód FL{id} ve zprávě).
 */

const fmtKc = (v) => new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' Kč';
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('cs-CZ') : '—');
const sklonuj = (n, [jeden, dva, hodne]) => (n === 1 ? jeden : n < 5 ? dva : hodne);

function Volba({ aktivni, onClick, children }) {
    return (
        <button type="button" onClick={onClick}
            className="flex-1 px-2 py-2 rounded-lg text-xs font-semibold"
            style={{
                background: aktivni ? `${SECTION_COLOR}18` : 'transparent',
                color: aktivni ? SECTION_COLOR : 'var(--text-tertiary)',
                border: `1px solid ${aktivni ? `${SECTION_COLOR}66` : 'var(--border-default)'}`,
            }}>
            {children}
        </button>
    );
}

function stahni(base64, nazev) {
    const bajty = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bajty], { type: 'application/octet-stream' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = nazev;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ExportAboModal({ ids, isDark, onClose, onHotovo }) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const [ucty, setUcty] = useState(null);
    const [ucetId, setUcetId] = useState(null);
    const [podleSplatnosti, setPodleSplatnosti] = useState(true);
    const [busy, setBusy] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [vysledek, setVysledek] = useState(null);

    useEffect(() => {
        fetch('/api/finance/banka/ucty', { headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : []))
            .then((data) => {
                const s = (Array.isArray(data) ? data : []).filter((u) => u.cislo_uctu);
                setUcty(s);
                setUcetId((s.find((u) => u.je_aktivni) ?? s[0])?.id ?? null);
            })
            .catch(() => setUcty([]));
    }, []);

    const exportovat = async () => {
        setBusy(true);
        setChyba(null);
        try {
            const r = await fetch('/api/finance/doklady/export-abo', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ ids, ucet_id: ucetId, podle_splatnosti: podleSplatnosti }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) { setChyba(data.error || data.message || 'Export selhal.'); return; }
            if (data.soubor) stahni(data.soubor, data.nazev);
            setVysledek(data);
        } catch {
            setChyba('Síťová chyba při exportu.');
        } finally {
            setBusy(false);
        }
    };

    const oznacit = async () => {
        setBusy(true);
        try {
            const r = await fetch('/api/finance/doklady/oznacit-platbu-zadanou', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ ids: vysledek.platby.map((p) => p.doklad_id), zadano: true }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) { setChyba(data.error || 'Označení selhalo.'); return; }
            onHotovo?.({ ...vysledek, oznaceno: data.zmeneno ?? 0 });
            onClose();
        } catch {
            setChyba('Síťová chyba při označování.');
        } finally {
            setBusy(false);
        }
    };

    const muted = 'var(--text-tertiary)';
    const pocet = vysledek?.platby?.length ?? 0;
    const bezVs = vysledek?.platby?.filter((p) => p.bez_vs) ?? [];

    const paticka = vysledek ? (
        <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { if (pocet) onHotovo?.({ ...vysledek, oznaceno: 0 }); onClose(); }} disabled={busy}
                className="px-3 py-2 rounded-lg text-sm font-medium" style={{ color: muted }}>
                {pocet ? 'Zatím neoznačovat' : 'Zavřít'}
            </button>
            {pocet > 0 && (
                <button type="button" onClick={oznacit} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold"
                    style={{ background: OK, color: '#fff', opacity: busy ? 0.6 : 1 }}>
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <SendHorizontal size={14} />}
                    Označit jako zadané do banky
                </button>
            )}
        </div>
    ) : (
        <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-sm font-medium" style={{ color: muted }}>Zrušit</button>
            <button type="button" onClick={exportovat} disabled={busy || !ucetId}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold"
                style={{ background: SECTION_COLOR, color: '#fff', opacity: busy || !ucetId ? 0.5 : 1 }}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Stáhnout soubor
            </button>
        </div>
    );

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Export pro banku" ikona={Landmark} barva={SECTION_COLOR}
            paticka={paticka} sirka={520}>
            <div className="p-4 flex flex-col gap-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                {!vysledek ? (
                    <>
                        <p className="m-0">
                            {ids.length} {sklonuj(ids.length, ['doklad', 'doklady', 'dokladů'])} jako hromadný příkaz (ABO, .kpc).
                            V George ho nahraješ a dávku jen autorizuješ. Doklady, které do banky nepatří
                            (hotovost, už zadané, bez účtu dodavatele, cizí měna), se přeskočí.
                        </p>
                        <div>
                            <div className="text-[11px] font-medium mb-1" style={{ color: muted }}>Platit z účtu</div>
                            {ucty === null ? (
                                <Loader2 size={16} className="animate-spin" style={{ color: SECTION_COLOR }} />
                            ) : ucty.length === 0 ? (
                                <div style={{ color: BAD }}>Není založený žádný náš účet — přidej ho ve Financích → Banka.</div>
                            ) : (
                                <Dropdown value={ucetId ?? ''} isDark={isDark} onChange={(v) => setUcetId(v ? parseInt(v) : null)}
                                    options={ucty.map((u) => ({ value: u.id, label: `${u.nazev} · ${u.cislo_uctu}${u.kod_banky ? '/' + u.kod_banky : ''}` }))} />
                            )}
                        </div>
                        <div>
                            <div className="text-[11px] font-medium mb-1" style={{ color: muted }}>Datum splatnosti</div>
                            <div className="flex gap-2">
                                <Volba aktivni={podleSplatnosti} onClick={() => setPodleSplatnosti(true)}>Podle splatnosti dokladů</Volba>
                                <Volba aktivni={!podleSplatnosti} onClick={() => setPodleSplatnosti(false)}>Všechno dnes</Volba>
                            </div>
                            <div className="text-[11px] mt-1" style={{ color: muted }}>
                                {podleSplatnosti
                                    ? 'Banka odešle každou platbu v den splatnosti; prošlé splatnosti dnes.'
                                    : 'Všechny platby odejdou dnes.'}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        {pocet > 0 ? (
                            <div className="flex items-start gap-2">
                                <Check size={16} style={{ color: OK, flexShrink: 0, marginTop: 2 }} />
                                <div>
                                    <b style={{ color: 'var(--text-primary)' }}>Staženo: {vysledek.nazev}</b> — {pocet} {sklonuj(pocet, ['platba', 'platby', 'plateb'])}, celkem {fmtKc(vysledek.celkem_kc)}.
                                    <div className="mt-1" style={{ color: muted }}>Nahraj ho v George a autorizuj. Pak doklady označ jako zadané, ať je nikdo nezadá podruhé.</div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ color: BAD }}>Žádný z vybraných dokladů nejde zaplatit hromadným příkazem — soubor se nevytvořil.</div>
                        )}
                        {pocet > 0 && (
                            <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
                                {vysledek.platby.map((p) => (
                                    <div key={p.doklad_id} className="flex items-center gap-2 px-3 py-1.5 text-xs"
                                        style={{ borderBottom: '1px solid var(--border-default)' }}>
                                        <span className="flex-1 min-w-0 truncate" style={{ color: 'var(--text-primary)' }}>{p.dodavatel || p.oznaceni}</span>
                                        <span style={{ color: muted }}>{fmtDate(p.datum)}</span>
                                        <span className="font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtKc(p.halere / 100)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {bezVs.length > 0 && (
                            <div className="flex items-start gap-2 text-xs" style={{ color: WARN }}>
                                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                                <span>Bez variabilního symbolu: {bezVs.map((p) => p.oznaceni).join(', ')} — dodavatel platbu nemusí spárovat.</span>
                            </div>
                        )}
                        {vysledek.preskoceno?.length > 0 && (
                            <div className="text-xs">
                                <div className="font-semibold mb-1" style={{ color: WARN }}>Přeskočeno ({vysledek.preskoceno.length}):</div>
                                <ul className="m-0 pl-4" style={{ color: muted }}>
                                    {vysledek.preskoceno.map((t, i) => <li key={i}>{t}</li>)}
                                </ul>
                            </div>
                        )}
                    </>
                )}
                {chyba && <div style={{ color: BAD }}>{chyba}</div>}
            </div>
        </ModalShell>
    );
}
